import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSiteDataApi } from "../utilities/SiteDataProvider";

/**
 * Styled MUI components
 */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  fontWeight: "bold",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * Public insurer names that get their own category
 */
const PUBLIC_INSURER_CATEGORIES = [
  "WSIB",
  "VAC",
  "First Canadian Health (NIHB)",
  "ODSP",
];

/**
 * Model keyword → category fallback (used when no invoice exists yet)
 */
const MODEL_KEYWORDS = {
  Elite: [
    "Vivia 9",
    "Nexia 9",
    "Edge AI 24",
    "Genesis AI 24",
    "Omega AI 24",
    "Intent 1",
    "7IX",
    "Starkey Signature Series 24",
    "Signature Series 24",
    "Naida L90",
    "Naida P90",
  ],
  Premium: [
    "Vivia 7",
    "Nexia 7",
    "Edge AI 20",
    "Genesis AI 20",
    "Omega AI 20",
    "Intent 2",
    "5IX",
    "Starkey Signature Series 20",
    "Signature Series 20",
    "Naida L70",
    "Naida P70",
  ],
  Select: [
    "Vivia 5",
    "Nexia 5",
    "Edge AI 16",
    "Genesis AI 16",
    "Omega AI 16",
    "Intent 3",
    "3IX",
    "Starkey Signature Series 16",
    "Signature Series 16",
    "Naida L50",
    "Naida P50",
  ],
  Custom: [
    "Savi 3",
    "Evolv AI 1200",
    "Evolv AI 1000",
    "SoundGear",
    "Naida L30",
    "Naida P30",
  ],
};

/** Estimated value per category (used when no invoice total is available) */
const CATEGORY_EST_VALUE = {
  Elite: 7499,
  Premium: 5999,
  Select: 4499,
  Custom: 3000,
  WSIB: 800,
  VAC: 1800,
  "First Canadian Health (NIHB)": 1325,
  ODSP: 1800,
};

/**
 * Determine category based on:
 *   1. Public insurer (overrides everything)
 *   2. Invoice order_total (when available)
 *   3. Model keyword matching (fallback for un-invoiced appointments)
 */
const getCategory = (model, insurer, orderTotal) => {
  const insurers = (insurer || "").split(",").map((s) => s.trim());

  // Public-insurer categories override everything
  for (const name of PUBLIC_INSURER_CATEGORIES) {
    if (insurers.includes(name)) return name;
  }

  // Categorize by invoice value when available
  const total = Number(orderTotal) || 0;
  if (total >= 7499) return "Elite";
  if (total >= 5999) return "Premium";
  if (total >= 4499) return "Select";
  if (total > 0) return "Custom";

  // Fallback: model keyword matching for appointments without invoices
  for (const [cat, keywords] of Object.entries(MODEL_KEYWORDS)) {
    if (keywords.some((k) => model.includes(k))) return cat;
  }

  return "Uncategorized";
};

/**
 * Generate a formatted date range string for the current month
 */
const getMonthRangeString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.toLocaleString("default", { month: "long" });
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  return `${month} 1–${lastDay}, ${year}`;
};

const TreatmentStartSummary = () => {
  const [categorySummary, setCategorySummary] = useState({});
  const [locationSummary, setLocationSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [siteDataContext] = useSiteDataApi();
  const monthRangeString = getMonthRangeString();

  /** Fetch & aggregate data on mount */
  useEffect(() => {
    const fetchTreatmentData = async () => {
      try {
        const res = await fetch("/api/upcoming-ts", {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": siteDataContext.thhcsfrtoken,
          },
        });
        const data = await res.json();
        const entries = data.returnData[0];

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59,
        );

        const catMap = {};
        const locMap = {};

        // Deduplicate by event_id (SQL joins can produce multiple rows per appointment)
        const seen = new Set();
        const uniqueEntries = entries.filter((entry) => {
          const id = entry.event_id;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        uniqueEntries.forEach((entry) => {
          const model = entry.model_name || "Unknown";
          const status = Number(entry.status);
          const orderTotal = Number(entry.order_total) || 0;
          const category = getCategory(
            model,
            entry.public_insurers,
            orderTotal,
          );
          const startDate = new Date(entry.start_time);
          startDate.setSeconds(0, 0);
          const location = entry.location_name || "Unknown";
          // Use real invoice amount when available, otherwise use estimated value
          const catVal =
            orderTotal > 0 ? orderTotal : CATEGORY_EST_VALUE[category] || 0;

          // Log uncategorized entries so we can identify them
          if (category === "Uncategorized") {
            console.log("[TS Uncategorized]", {
              model,
              insurers: entry.public_insurers,
              orderTotal,
              location,
              fullEntry: entry,
            });
          }

          const dateOnly = (d) =>
            new Date(d.getFullYear(), d.getMonth(), d.getDate());

          const isStarted =
            status === 7 &&
            dateOnly(startDate) >= dateOnly(startOfMonth) &&
            dateOnly(startDate) <= dateOnly(yesterday);

          const isRemaining =
            status !== 7 &&
            dateOnly(startDate) >= dateOnly(today) &&
            dateOnly(startDate) <= dateOnly(endOfMonth);

          // Init buckets
          if (!catMap[category]) {
            catMap[category] = {
              thisMonth: 0,
              remaining: 0,
              valueSoFar: 0,
              projectedValue: 0,
            };
          }
          if (!locMap[location]) {
            locMap[location] = { valueSoFar: 0, restOfMonth: 0, monthTotal: 0 };
          }

          // Category aggregation
          if (isStarted) {
            catMap[category].thisMonth += 1;
            catMap[category].valueSoFar += catVal;
            catMap[category].projectedValue += catVal;
          }
          if (isRemaining) {
            catMap[category].remaining += 1;
            catMap[category].projectedValue += catVal;
          }

          // Location aggregation
          if (isStarted) {
            locMap[location].valueSoFar += catVal;
            locMap[location].monthTotal += catVal;
          }
          if (isRemaining) {
            locMap[location].restOfMonth += catVal;
            locMap[location].monthTotal += catVal;
          }
        });

        setCategorySummary(catMap);
        setLocationSummary(locMap);
      } catch (err) {
        console.error("Failed to fetch treatment data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTreatmentData();
  }, []);

  if (loading) {
    return (
      <Box mt={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading treatment summary...</Typography>
      </Box>
    );
  }

  /** Prepare rows & totals */
  const catRows = Object.entries(categorySummary);
  const catTotals = catRows.reduce(
    (acc, [, v]) => {
      acc.thisMonth += v.thisMonth;
      acc.remaining += v.remaining;
      acc.valueSoFar += v.valueSoFar;
      acc.projectedValue += v.projectedValue;
      return acc;
    },
    { thisMonth: 0, remaining: 0, valueSoFar: 0, projectedValue: 0 },
  );

  const locRows = Object.entries(locationSummary);
  const locTotals = locRows.reduce(
    (acc, [, v]) => {
      acc.valueSoFar += v.valueSoFar;
      acc.restOfMonth += v.restOfMonth;
      acc.monthTotal += v.monthTotal;
      return acc;
    },
    { valueSoFar: 0, restOfMonth: 0, monthTotal: 0 },
  );

  /** Render tables */
  return (
    <Box m={3}>
      <Typography variant="h5" gutterBottom>
        Treatment Start Summary ({monthRangeString})
      </Typography>

      {/* -------- CATEGORY TABLE -------- */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Category</StyledTableCell>
              <StyledTableCell align="center">
                Started This Month
              </StyledTableCell>
              <StyledTableCell align="center">Value So Far</StyledTableCell>
              <StyledTableCell align="center">Remaining Starts</StyledTableCell>
              <StyledTableCell align="center">Projected Value</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {catRows.map(([cat, v]) => (
              <StyledTableRow key={cat}>
                <TableCell>{cat}</TableCell>
                <TableCell align="center">{v.thisMonth}</TableCell>
                <TableCell align="center">
                  ${v.valueSoFar.toLocaleString()}
                </TableCell>
                <TableCell align="center">{v.remaining}</TableCell>
                <TableCell align="center">
                  ${v.projectedValue.toLocaleString()}
                </TableCell>
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCell>Total</StyledTableCell>
              <StyledTableCell align="center">
                {catTotals.thisMonth}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${catTotals.valueSoFar.toLocaleString()}
              </StyledTableCell>
              <StyledTableCell align="center">
                {catTotals.remaining}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${catTotals.projectedValue.toLocaleString()}
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* -------- LOCATION TABLE -------- */}
      <Typography variant="h6" gutterBottom>
        Revenue by Location ({monthRangeString})
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Location</StyledTableCell>
              <StyledTableCell align="center">Value So Far</StyledTableCell>
              <StyledTableCell align="center">Rest of Month</StyledTableCell>
              <StyledTableCell align="center">Month Total</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {locRows.map(([loc, v]) => (
              <StyledTableRow key={loc}>
                <TableCell>{loc}</TableCell>
                <TableCell align="center">
                  ${v.valueSoFar.toLocaleString()}
                </TableCell>
                <TableCell align="center">
                  ${v.restOfMonth.toLocaleString()}
                </TableCell>
                <TableCell align="center">
                  ${v.monthTotal.toLocaleString()}
                </TableCell>
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCell>Total</StyledTableCell>
              <StyledTableCell align="center">
                ${locTotals.valueSoFar.toLocaleString()}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${locTotals.restOfMonth.toLocaleString()}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${locTotals.monthTotal.toLocaleString()}
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TreatmentStartSummary;
